import React, { useState } from 'react';
import { Bill } from '../../types/bill.ts';
import { formatCurrency, formatDate, formatMonthYear } from '../../utils/formatters.ts';
import PaymentForm from '../payments/PaymentForm.tsx';
import { useNavigate } from 'react-router-dom';
import BillDetails from './BillDetails.tsx';
import ConfirmationModal from '../shared/ConfirmationModal.tsx';

interface BillListProps {
  bills: Bill[];
  onUpdate: (id: string, status: Bill['status']) => Promise<void>;
  onRefresh: () => void;
  dateRange: {
    from: string;
    to: string;
  };
  statusFilter: string;
}

const BillList: React.FC<BillListProps> = ({ bills, onUpdate, onRefresh, dateRange, statusFilter }) => {
  const navigate = useNavigate();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedBill, setSelectedBill] = useState<Bill | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [selectedBillId, setSelectedBillId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentFormData, setPaymentFormData] = useState<{
    tenantId?: string;
    billId?: string;
    amount?: number;
  } | null>(null);
  const ITEMS_PER_PAGE = 10;

  // Sort bills before filtering
  const sortedBills = [...bills].sort((a, b) => {
    // First compare by due date
    const dateComparison = new Date(b.due_date).getTime() - new Date(a.due_date).getTime();
    if (dateComparison !== 0) return dateComparison;
    
    // If due dates are same, compare by creation time
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const filteredBills = sortedBills.filter(bill => {
    const billDate = new Date(bill.created_at);
    const fromDate = dateRange.from ? new Date(dateRange.from) : null;
    const toDate = dateRange.to ? new Date(dateRange.to) : null;
    
    // Status filter
    if (statusFilter !== 'all' && bill.status !== statusFilter) {
      return false;
    }

    // Date range filter
    if (fromDate && toDate) {
      return billDate >= fromDate && billDate <= toDate;
    } else if (fromDate) {
      return billDate >= fromDate;
    } else if (toDate) {
      return billDate <= toDate;
    }

    return true;
  });

  const paginatedBills = filteredBills.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredBills.length / ITEMS_PER_PAGE);

  const getStatusClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'status-paid';
      case 'unpaid':
        return 'status-unpaid';
      case 'partial':
        return 'status-partial';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return '';
    }
  };

  const handleStatusChange = async (e: React.MouseEvent, billId: string, newStatus: Bill['status']) => {
    e.stopPropagation(); // Stop event bubbling
    setSelectedBillId(billId);
    setShowCancelConfirm(true);
  };

  const confirmCancelBill = async () => {
    if (!selectedBillId) return;
    
    setIsCancelling(true);
    try {
      await onUpdate(selectedBillId, 'Cancelled');
    } catch (error) {
      console.error('Error updating bill status:', error);
      alert('Failed to update bill status');
    } finally {
      setIsCancelling(false);
      setShowCancelConfirm(false);
      setSelectedBillId(null);
    }
  };

  const handleBillClick = (billId: string) => {
    navigate(`/bills/${billId}`);
  };

  const handleRefetch = async () => {
    await onRefresh();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRecordPayment = (e: React.MouseEvent, bill: Bill) => {
    e.stopPropagation();
    setPaymentFormData({
      tenantId: bill.tenant_id,
      billId: bill.id,
      amount: bill.amount - (bill.paid_amount || 0)
    });
    setShowPaymentForm(true);
  };


  return (
    <div className="bills-container">
      <div className="table-responsive">
        {/* Desktop View */}
        <table className="bill-table desktop-only">
          <thead>
            <tr>
              <th className="date-column">Month</th>
              <th className="name-column">Tenant</th>
              <th className="name-column">Property</th>
              <th>Total Amount</th>
              <th>Due Amount</th>
              <th className="date-column">Due Date</th>
              <th>Status</th>
              <th>Actions</th>
              <th>Payment</th>
            </tr>
          </thead>
          <tbody>
            {paginatedBills.map((bill) => (
              <tr key={bill.id} onClick={() => handleBillClick(bill.id)} className="bill-row">
                <td className="date-column">{formatMonthYear(bill.month)}</td>
                <td className="name-column">{bill.tenant?.full_name}</td>
                <td className="name-column">{bill.property?.name}</td>
                <td>{formatCurrency(bill.amount)}</td>
                <td>{formatCurrency(bill.dueAmount ?? 0)}</td>
                <td className="date-column">{formatDate(bill.due_date)}</td>
                <td>
                  <span className={`status-badge ${getStatusClass(bill.status)}`}>
                    <span className="status-dot"></span>
                    {bill.status}
                  </span>
                </td>
                <td>
                  <div className="bill-actions">
                    <button
                      className="btn-warning"
                      onClick={(e) => handleStatusChange(e, bill.id, 'Cancelled')}
                      disabled={['Paid', 'Cancelled', 'Partial'].includes(bill.status)}
                    >
                      Cancel
                    </button>
                  </div>
                </td>
                <td>
                  <button
                    className="btn-secondary"
                    onClick={(e) => handleRecordPayment(e, bill)}
                    disabled={['Paid', 'Cancelled'].includes(bill.status)}
                  >
                    Record Payment
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Mobile View */}
        <div className="bill-cards-container mobile-only">
          {paginatedBills.map((bill) => (
            <div key={bill.id} className="bill-card" onClick={() => handleBillClick(bill.id)}>
              <div className="bill-card-header">
                <span className="bill-card-date">{formatMonthYear(bill.month)}</span>
                <span className={`status-badge ${getStatusClass(bill.status)}`}>
                  <span className="status-dot"></span>
                  {bill.status}
                </span>
              </div>
              
              <div className="bill-card-body">
                <div className="bill-card-row">
                  <label>Tenant:</label>
                  <span>{bill.tenant?.full_name}</span>
                </div>
                <div className="bill-card-row">
                  <label>Property:</label>
                  <span>{bill.property?.name}</span>
                </div>
                <div className="bill-card-row">
                  <label>Total Amount:</label>
                  <span className="bill-amount">{formatCurrency(bill.amount)}</span>
                </div>
                <div className="bill-card-row">
                  <label>Due Amount:</label>
                  <span className="bill-amount">{formatCurrency(bill.dueAmount ?? 0)}</span>
                </div>
                <div className="bill-card-row">
                  <label>Due Date:</label>
                  <span>{formatDate(bill.due_date)}</span>
                </div>
              </div>

              <div className="bill-card-actions">
                <button
                  className="btn-warning"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStatusChange(e, bill.id, 'Cancelled');
                  }}
                  disabled={['Paid', 'Cancelled', 'Partial'].includes(bill.status)}
                >
                  Cancel
                </button>
                <button
                  className="btn-secondary"
                  onClick={(e) => handleRecordPayment(e, bill)}
                  disabled={['Paid', 'Cancelled'].includes(bill.status)}
                >
                  Record Payment
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredBills.length > ITEMS_PER_PAGE && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary"
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showPaymentForm && (
        <PaymentForm
          initialData={paymentFormData}
          onClose={() => {
            setShowPaymentForm(false);
            setPaymentFormData(null);
          }}
          onRefetch={handleRefetch}
        />
      )}

      {selectedBill && (
        <BillDetails 
          onSubmit={async (data) => {
            await onRefresh();
            setSelectedBill(null);
          }}
          onClose={() => setSelectedBill(null)}
        />
      )}

      <ConfirmationModal
        isOpen={showCancelConfirm}
        message="Are you sure you want to cancel this bill? This action cannot be undone and the bill will be marked as cancelled."
        onConfirm={confirmCancelBill}
        onCancel={() => {
          setShowCancelConfirm(false);
          setSelectedBillId(null);
        }}
        confirmText="Cancel Bill"
        cancelText="Close"
        isLoading={isCancelling}
      />
    </div>
  );
};

export default BillList;
